import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, Flag, MoreHorizontal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { AuthContext } from '../../../contexts/AuthContext';
import { deleteComment, reportComment } from '../../../database/profileComments';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { Badge } from '../../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const initials = (s) => {
  const v = (s || '').trim();
  if (!v) return '?';
  const parts = v.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2)).toUpperCase();
};

const formatRelative = (ts) => {
  if (!ts?.toDate) return '';
  const d = ts.toDate();
  const diff = Math.max(0, Date.now() - d.getTime());
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

const CommentItem = ({ comment, ownerUserId }) => {
  const { currentUser, hasPermission } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  const isAuthor = currentUser?.uid === comment.authorUserId;
  const isWallOwner = currentUser?.uid === ownerUserId;
  const isMod = hasPermission('profileComments.moderate');
  const isHidden = comment.status === 'hidden';
  const isRemoved = comment.status === 'removed';
  const reportedByMe = !!currentUser && (comment.reportedBy || []).includes(currentUser.uid);

  // For non-mod reporters, hidden comments default to a placeholder; reveal flips it open.
  const hideTextForReporter = isHidden && reportedByMe && !isMod && !revealed;

  const canDelete = isAuthor || isWallOwner || isMod;
  const canReport = !!currentUser && !isAuthor && comment.status === 'visible';

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;
    setBusy(true);
    try {
      await deleteComment(comment.id);
      toast.success('Comment deleted.');
    } catch (err) {
      toast.error('Delete failed.', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  const handleReport = async () => {
    setBusy(true);
    try {
      await reportComment(comment.id, currentUser.uid);
      toast.success('Comment reported. Mods will review it.');
    } catch (err) {
      toast.error('Report failed.', { description: err.message });
    } finally {
      setBusy(false);
    }
  };

  // Compact placeholder shown to the reporter when a comment is hidden.
  if (hideTextForReporter) {
    return (
      <li className="flex items-center justify-between gap-3 border-b border-border/60 py-3 last:border-b-0 text-sm text-muted-foreground">
        <span className="inline-flex items-center gap-2">
          <EyeOff className="h-3.5 w-3.5" />
          You reported this comment. It's hidden pending review.
        </span>
        <button
          type="button"
          onClick={() => setRevealed(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors hover:border-foreground/40 hover:text-foreground"
        >
          <Eye className="h-3 w-3" />
          Show
        </button>
      </li>
    );
  }

  return (
    <li className="grid grid-cols-[auto_1fr] gap-3 border-b border-border/60 py-4 last:border-b-0">
      <Link to={`/user/${comment.authorUserId}`} className="shrink-0">
        <Avatar className="h-9 w-9">
          {comment.authorPhotoURL && <AvatarImage src={comment.authorPhotoURL} alt="" />}
          <AvatarFallback className="text-xs">{initials(comment.authorUsername)}</AvatarFallback>
        </Avatar>
      </Link>

      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Link
              to={`/user/${comment.authorUserId}`}
              className="truncate font-display text-sm font-medium hover:underline"
            >
              {comment.authorUsername || 'Player'}
            </Link>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              {formatRelative(comment.createdAt)}
            </span>
            {isHidden && (
              <Badge variant="outline" className="text-warning">
                Hidden · pending review
              </Badge>
            )}
            {isRemoved && (
              <Badge variant="outline" className="text-destructive">
                Removed by mod
              </Badge>
            )}
          </div>

          {(canDelete || canReport || (isHidden && reportedByMe && !isMod)) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={busy}
                  className="rounded-md p-1 text-muted-foreground hover:bg-secondary/60 hover:text-foreground disabled:opacity-50"
                  aria-label="Comment actions"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canReport && (
                  <DropdownMenuItem onSelect={handleReport}>
                    <Flag className="mr-2 h-3.5 w-3.5" />
                    Report
                  </DropdownMenuItem>
                )}
                {isHidden && reportedByMe && !isMod && (
                  <DropdownMenuItem onSelect={() => setRevealed(false)}>
                    <EyeOff className="mr-2 h-3.5 w-3.5" />
                    Hide again
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem onSelect={handleDelete} className="text-destructive">
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <p className="mt-1 whitespace-pre-wrap break-words text-sm text-foreground">
          {comment.text}
        </p>
      </div>
    </li>
  );
};

export default CommentItem;
